import { JoonUIColor } from "@joonapp/web-shared"
import { Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useEffect } from "react"
import { useState } from "react"

import { useScheduledMessagesQuery } from "../../networking/scheduledMessages/queries"
import useCurrentClient from "../../pages/clients/hooks/useCurrentClient"
import useScheduledMessageModalStore from "../../pages/clients/routines/useScheduledMessageModalStore"
import {
  getFormattedTimeInTimezone,
  getRelativeTimeUntil,
} from "../../util/time"

// - Displays the next scheduled message for a user.
// - Determines the next message scheduled after the current time.
// - Updates every minute to ensure accuracy.
// - Allows users to view or edit the message via a button that opens a modal.

const NextScheduledMessage = () => {
  const { data: scheduledMessages } = useScheduledMessagesQuery()
  const { onOpen: openScheduledMessageModal } = useScheduledMessageModalStore()
  const [currentTime, setCurrentTime] = useState(dayjs())
  const { currentClientProfile } = useCurrentClient()

  useEffect(() => {
    // Update the current time every minute
    const interval = setInterval(() => {
      setCurrentTime(dayjs())
    }, 60000) // 60000 ms = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(interval)
  }, [])

  // Memoize the calculation of the next scheduled message
  const nextScheduledMessage = useMemo(() => {
    return (
      scheduledMessages
        ?.filter((message) => dayjs(message.datetime).isAfter(currentTime))
        .sort((a, b) => dayjs(a.datetime).diff(dayjs(b.datetime)))?.[0] ?? null
    )
  }, [scheduledMessages, currentTime])

  if (!nextScheduledMessage) return null
  if (!currentClientProfile?.user.timezone) return null

  const handleViewMessage = () => {
    if (!currentClientProfile?.user.timezone) return
    openScheduledMessageModal({
      scheduledMessage: nextScheduledMessage,
      timezone: currentClientProfile?.user.timezone,
    })
  }

  const clientTime = getFormattedTimeInTimezone(
    nextScheduledMessage.datetime,
    currentClientProfile?.user.timezone
  )

  return (
    <div className="pt-2 flex flex-row items-center gap-1">
      <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
        Next scheduled message:{" "}
        {getRelativeTimeUntil(nextScheduledMessage.datetime, true)} (
        {clientTime} for {currentClientProfile?.user.name})
      </Typography>
      <button onClick={handleViewMessage}>
        <Typography variant="bodySmall" color={JoonUIColor.text.primaryAccent}>
          View or edit
        </Typography>
      </button>
    </div>
  )
}

export default NextScheduledMessage
